import { useCallback, useRef } from "react";

const DEFAULT_THRESHOLD_MS = 500;
const MOVE_CANCEL_THRESHOLD_PX = 10;

// Pointer-events based so it works uniformly across touch, mouse, and pen —
// no device detection needed. Harmless when attached on desktop (a mouse
// "long-press" just also selects, alongside the still-visible checkbox);
// on touch it's the only way to select once the checkbox is hidden (see
// each card's own @media (pointer: coarse) rule on its checkbox).
//
// wasLongPress() is an imperative, one-shot check rather than relying on
// stopPropagation between an onClickCapture and a sibling onClick on the
// same element — that ordering is easy to get subtly wrong. The card's own
// click handler calls wasLongPress() first and bails out if a long-press
// just fired, before doing its normal tap behavior.
export default function useLongPress(onLongPress, { thresholdMs = DEFAULT_THRESHOLD_MS } = {}) {
  const timerRef = useRef(null);
  const startPointRef = useRef(null);
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startPointRef.current = null;
  }, []);

  const onPointerDown = useCallback(
    (event) => {
      firedRef.current = false;
      startPointRef.current = { x: event.clientX, y: event.clientY };
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        onLongPress(event);
      }, thresholdMs);
    },
    [onLongPress, thresholdMs],
  );

  const onPointerMove = useCallback(
    (event) => {
      if (!startPointRef.current) return;
      const dx = event.clientX - startPointRef.current.x;
      const dy = event.clientY - startPointRef.current.y;
      if (Math.hypot(dx, dy) > MOVE_CANCEL_THRESHOLD_PX) clear();
    },
    [clear],
  );

  const onPointerUp = useCallback(() => clear(), [clear]);
  const onPointerCancel = useCallback(() => clear(), [clear]);
  const onPointerLeave = useCallback(() => clear(), [clear]);

  const wasLongPress = useCallback(() => {
    const was = firedRef.current;
    firedRef.current = false;
    return was;
  }, []);

  if (!onLongPress) {
    return { wasLongPress: () => false };
  }

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave, wasLongPress };
}
